import React, { useState, useRef, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlay, faPause, faVolumeMute, faVolumeDown, faVolumeUp, faEllipsisV } from '@fortawesome/free-solid-svg-icons'
import { saveVolume, startFadeIn, startFadeOut, completeFade } from '../../reducers/sound'

const AudioPlayer = ({ componentState, dispatch }) => {
    const [isPlaying, setIsPlaying] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [volume, setVolume] = useState(0)
    const [showVolumeSlider, setShowVolumeSlider] = useState(false)
    const [audioSourceURL] = useState('https://www.learningcontainer.com/wp-content/uploads/2020/02/Kalimba.mp3')
    const audioRef = useRef(null)
    const volumeRef = useRef(null)
    const { volumeLevel } = useSelector(state => state.sound)

    useEffect(() => {
        setIsPlaying(false)
    }, [audioSourceURL])

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = volumeLevel
        }
    }, [volumeLevel])

    useEffect(() => {
        let fadeInterval = null
        if (componentState === 'ANIMATING_IN') {
            if (audioRef.current) {
                let currentVolume = 0
                audioRef.current.volume = currentVolume
                dispatch(startFadeIn())

                fadeInterval = setInterval(() => {
                    if (audioRef.current) {
                        currentVolume = Math.min(currentVolume + 0.01, 1.0)
                        audioRef.current.volume = currentVolume
                        dispatch(saveVolume({ volumeLevel: currentVolume }))

                        if (currentVolume >= 1.0) {
                            clearInterval(fadeInterval)
                            dispatch(completeFade())
                        }
                    }
                }, 50)
            }
        }

        return () => {
            if (fadeInterval) {
                clearInterval(fadeInterval)
            }
        }
    }, [componentState])

    // Handle fade out
    useEffect(() => {
        let fadeInterval = null
        let isUnmounting = false
        if (componentState === 'ANIMATING_OUT') {
            if (audioRef.current) {
                let currentVolume = audioRef.current.volume
                dispatch(startFadeOut())

                fadeInterval = setInterval(() => {
                    if (audioRef.current && !isUnmounting) {
                        currentVolume = Math.max(currentVolume - 0.02, 0.0)
                        audioRef.current.volume = currentVolume
                        dispatch(saveVolume({ volumeLevel: currentVolume }))

                        if (currentVolume <= 0) {
                            clearInterval(fadeInterval)
                            dispatch(completeFade())
                        }
                    }
                }, 20)
            }
        }

        return () => {
            isUnmounting = true
            if (fadeInterval) {
                clearInterval(fadeInterval)
            }
        }
    }, [componentState])


    const togglePlayPause = () => {
        if (audioRef.current) {
            if (audioRef.current.paused) {
                audioRef.current?.play()
                setIsPlaying(true)
            } else {
                audioRef.current?.pause()
                setIsPlaying(false)
            }
        }
    }

    const handleOnPlay = () => {
        setIsPlaying(true)
    }

    const handleEnded = () => {
        setIsPlaying(false)
        if (audioRef.current) {
            audioRef.current.currentTime = 0
        }
    }

    const handleTimeUpdate = () => {
        setCurrentTime(audioRef.current.currentTime)
    }

    const handleLoadedMetadata = () => {
        setDuration(audioRef.current.duration)
    }

    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value)
        if (audioRef.current) {
            audioRef.current.volume = newVolume
        }
        setVolume(newVolume)
    }

    const getVolumeIcon = () => {
        if (volumeLevel === 0) {
            return faVolumeMute
        } else if (volumeLevel > 0 && volumeLevel <= 0.7) {
            return faVolumeDown
        }
        return faVolumeUp
    }
    const formatTime = (time) => {
        const minutes = Math.floor(time / 60)
        const seconds = Math.floor(time % 60)
        return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (volumeRef.current && !volumeRef.current.contains(event.target)) {
                setShowVolumeSlider(false)
            }
        }

        document.addEventListener('click', handleClickOutside)
        return () => {
            document.removeEventListener('click', handleClickOutside)
        }
    }, [])

    if (componentState === 'PRELOADING') {
        return null
    }

    if (!audioSourceURL) {
        return (
            <div className="label-header" style={{ color: 'red' }}>
                Please enter audio source URL
            </div>
        )
    }

    return (
        <div className="audio-player">
            <audio
                ref={audioRef}
                src={audioSourceURL}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={handleOnPlay}
                onEnded={handleEnded}
                autoPlay={true}
            />

            <span className="btn icon small" onClick={togglePlayPause}>
                <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
            </span>

            <span className="time-display">
                <span className="time-running">{formatTime(currentTime)}</span> / {formatTime(duration)}
            </span>

            <input
                type="range"
                className="progress-bar"
                value={currentTime}
                max={duration}
                onChange={(e) => {
                    audioRef.current.currentTime = e.target.value
                    setCurrentTime(e.target.value)
                }}
            />

            <div className="volume-control" ref={volumeRef} style={{ position: 'relative' }}>
                <FontAwesomeIcon
                    icon={getVolumeIcon()}
                    className="volume-icon"
                    onClick={() => setShowVolumeSlider(!showVolumeSlider)}
                />
                {showVolumeSlider && (
                    <input
                        type="range"
                        className="volume-slider"
                        min="0"
                        max="1"
                        step="0.01"
                        value={volume}
                        onChange={handleVolumeChange}
                    />
                )}
            </div>

            <FontAwesomeIcon icon={faEllipsisV} className="options-icon" />
        </div>
    )
}

export default AudioPlayer
